import { useRefreshTick } from "../lib/useAutoRefresh";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { money, FREQ_LABELS } from "../lib/format";
import { ArrowRight, PlayCircle, CalendarClock, AlertTriangle, UserPlus, Landmark } from "lucide-react";
import { useSort, SortBtn } from "../lib/sortable";
import { HeldFundsCard } from "./TrustBalance";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtDue = (iso) => { if (!iso) return ""; const [y, m, d] = iso.split("-"); return `${MON[Number(m) - 1]} ${Number(d)}`; };

// Compact one-line CRA status. Values are backend-authoritative (Phase 4.1) — no recompute here.
const craLine = (o) => {
  const dot = (c) => `inline-block h-2 w-2 rounded-full ${c} mr-1.5`;
  switch (o.status) {
    case "CURRENT": return { color: "text-emerald-700", dot: dot("bg-emerald-500"), text: "CRA current" };
    case "NO_SCHEDULE": return { color: "text-muted-foreground", dot: dot("bg-neutral-300"), text: "CRA — no remittance due yet" };
    case "ADJUSTMENT_REQUIRED": return { color: "text-purple-700", dot: dot("bg-purple-500"), text: "CRA adjustment required" };
    case "OVERDUE": return {
      color: "text-red-700", dot: dot("bg-red-500"),
      text: o.overdue_count > 1 ? `${o.overdue_count} CRA remittances overdue`
        : `CRA ${money(o.balance_due)} overdue by ${Math.abs(o.days)} day${Math.abs(o.days) === 1 ? "" : "s"}` };
    case "DUE_TODAY": return { color: "text-orange-700", dot: dot("bg-orange-500"), text: `CRA ${money(o.balance_due)} due today` };
    case "DUE_SOON": return { color: "text-amber-700", dot: dot("bg-amber-500"), text: `CRA ${money(o.balance_due)} due in ${o.days} day${o.days === 1 ? "" : "s"}` };
    default: return { color: "text-neutral-700", dot: dot("bg-neutral-400"), text: `CRA ${money(o.balance_due)} due ${fmtDue(o.due_date)}` };
  }
};
const remitHref = (o) => {
  const base = `/remittance?company=${o.company_id}`;
  return o.has_obligation && o.period_key ? `${base}&period=${encodeURIComponent(o.period_key)}&pyear=${o.year}` : base;
};

const UP_A = {
  company: (u) => u.company_name || "", schedule: (u) => u.schedule_name || "",
  frequency: (u) => FREQ_LABELS[u.pay_frequency] || "", period: (u) => u.pay_period_start || "", pay_date: (u) => u.pay_date || "", due_by: (u) => u.due_by || "",
};
const RR_A = {
  company: (r) => r.company_name || "", pay_date: (r) => r.pay_date || "", frequency: (r) => FREQ_LABELS[r.pay_frequency] || "",
  emp: (r) => r.employee_count || 0, gross: (r) => r.totals?.gross || 0, ded: (r) => r.totals?.total_deductions || 0, net: (r) => r.totals?.net_pay || 0,
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [remit, setRemit] = useState([]);
  const [filter, setFilter] = useState("");
  const navigate = useNavigate();
  const up = useSort(data?.upcoming_runs || [], UP_A, "pay_date", "asc");
  const rr = useSort(data?.recent_runs || [], RR_A, "pay_date", "desc");

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/dashboard/remittance-obligations").then((r) => setRemit(r.data?.companies || [])).catch(() => {});
  }, []);

  const tick = useRefreshTick();
  useEffect(() => {
    api.get(`/dashboard${filter ? `?company_id=${filter}` : ""}`).then((r) => setData(r.data)).catch(() => {});
    api.get("/dashboard/remittance-obligations").then((r) => setRemit(r.data?.companies || [])).catch(() => {});
  }, [filter, tick]);

  return (
    <div className="w-full">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Payroll Console</h1>
          <p className="text-sm text-muted-foreground mt-1 font-mono uppercase tracking-widest">Overview · Fiscal 2026</p>
        </div>
      </div>

      {/* New-hire onboarding alerts */}
      {data?.pending_onboarding?.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 mb-6" data-testid="onboarding-alerts">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-amber-200">
            <UserPlus className="h-4 w-4 text-amber-700" />
            <h2 className="font-bold tracking-tight text-amber-900">New Hires Awaiting Review</h2>
            <span data-testid="onboarding-pending-total" className="text-[10px] font-mono uppercase tracking-widest bg-amber-200 text-amber-800 px-2 py-1">
              {data.onboarding_pending_total} pending
            </span>
          </div>
          <div className="divide-y divide-amber-200">
            {data.pending_onboarding.map((p) => (
              <Link
                key={p.company_id}
                to={`/companies/${p.company_id}`}
                data-testid={`onboarding-alert-${p.company_id}`}
                className="flex items-center justify-between px-5 py-3 hover:bg-amber-100 transition-colors"
              >
                <div className="text-sm font-medium text-amber-900">{p.company_name}</div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono text-amber-700">{p.count} submission{p.count === 1 ? "" : "s"}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-900">Review <ArrowRight className="h-3.5 w-3.5" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming payroll runs — one click to start */}
      <div className="border border-border bg-white mb-6" data-testid="upcoming-runs">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-bold tracking-tight flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Upcoming Payroll Runs</h2>
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Next run per schedule</span>
        </div>
        {data?.upcoming_runs?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  <th className="px-5 py-3"><SortBtn label="Client Company" col="company" sort={up.sort} onSort={up.toggle} /></th>
                  <th className="px-5 py-3"><SortBtn label="Schedule" col="schedule" sort={up.sort} onSort={up.toggle} /></th>
                  <th className="px-5 py-3"><SortBtn label="Frequency" col="frequency" sort={up.sort} onSort={up.toggle} /></th>
                  <th className="px-5 py-3"><SortBtn label="Pay Period" col="period" sort={up.sort} onSort={up.toggle} /></th>
                  <th className="px-5 py-3"><SortBtn label="Due By" col="due_by" sort={up.sort} onSort={up.toggle} /></th>
                  <th className="px-5 py-3"><SortBtn label="Pay Date" col="pay_date" sort={up.sort} onSort={up.toggle} /></th>
                  <th className="px-5 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {up.sorted.map((u, i) => (
                  <tr key={`${u.company_id}-${u.schedule_name}-${i}`} data-testid={`upcoming-run-${u.company_id}`} className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors">
                    <td className="px-5 py-3 font-medium"><Link data-testid={`upcoming-company-link-${u.company_id}`} to={`/companies/${u.company_id}`} className="hover:underline underline-offset-2">{u.company_name}</Link></td>
                    <td className="px-5 py-3 text-muted-foreground">
                      {u.schedule_name || "—"}
                      {u.based_on && <div className="text-[10px] font-mono" title="Last effective run this is sequenced from">after {u.based_on}{u.based_on_status && !/POSTED|PAID/.test(u.based_on_status) ? ` · ${u.based_on_status.toLowerCase()} draft` : ""}</div>}
                    </td>
                    <td className="px-5 py-3">{FREQ_LABELS[u.pay_frequency]}</td>
                    <td className="px-5 py-3 font-mono text-xs">{u.pay_period_start} → {u.pay_period_end}</td>
                    <td className="px-5 py-3 font-mono text-xs" data-testid={`upcoming-due-by-${u.company_id}`}>
                      <span className={`flex items-center gap-1.5 ${u.past_due_by ? "text-red-700 font-semibold" : ""}`}>
                        {u.due_by}
                        {u.past_due_by && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-red-700 bg-red-100 px-1.5 py-0.5" title="Past the processor deadline — payment to employees may be delayed">
                            <AlertTriangle className="h-3 w-3" /> Past due
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-mono">
                      <span className="flex items-center gap-1.5">
                        {u.pay_date}
                        {u.overdue && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-amber-700 bg-amber-100 px-1.5 py-0.5" title="Due today or overdue">
                            <AlertTriangle className="h-3 w-3" /> Due
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        data-testid={`start-run-${u.company_id}`}
                        onClick={() => navigate(`/run-payroll?company=${u.company_id}${u.schedule_name ? `&schedule=${encodeURIComponent(u.schedule_name)}` : ""}`)}
                        className="inline-flex items-center gap-1.5 bg-black text-white px-3 py-1.5 text-xs font-medium hover:bg-neutral-800 transition-colors"
                      >
                        <PlayCircle className="h-3.5 w-3.5" /> Start
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-5 py-12 text-center text-sm text-muted-foreground">No upcoming runs — add a client company with a pay schedule to see it here.</div>
        )}
      </div>

      {/* Held funds (full payroll funding) with CRA / WSIB / EHT due dates — master only */}
      <HeldFundsCard />

      {/* CRA remittance obligations — compact per-company urgency (batched, authoritative) */}
      {remit.length > 0 && (
        <div className="border border-border bg-white mb-6" data-testid="dashboard-remittance-obligations">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <h2 className="font-bold tracking-tight flex items-center gap-2"><Landmark className="h-4 w-4" /> CRA Remittances</h2>
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Next obligation per company</span>
          </div>
          <div className="divide-y divide-border">
            {remit.map((o) => {
              const line = craLine(o);
              return (
                <Link key={o.company_id} to={remitHref(o)} data-testid={`remit-obligation-${o.company_id}`}
                  className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-muted/50 transition-colors">
                  <div className="text-sm font-medium truncate">{o.company_name}</div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span data-testid={`remit-ob-status-${o.company_id}`} className={`inline-flex items-center text-xs font-medium ${line.color}`}>
                      <span className={line.dot} />{line.text}
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div className="border border-border bg-white">
        <div className="flex flex-wrap items-center gap-3 justify-between px-5 py-4 border-b border-border">
          <h2 className="font-bold tracking-tight">Recent Payroll Runs</h2>
          <div className="flex items-center gap-3">
            <select data-testid="dashboard-company-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black">
              <option value="">All companies</option>
              {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
            </select>
            <Link to="/companies" className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-black flex items-center gap-1">
              Companies <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
        {data?.recent_runs?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  <th className="px-5 py-3"><SortBtn label="Client Company" col="company" sort={rr.sort} onSort={rr.toggle} /></th>
                  <th className="px-5 py-3"><SortBtn label="Pay Date" col="pay_date" sort={rr.sort} onSort={rr.toggle} /></th>
                  <th className="px-5 py-3"><SortBtn label="Frequency" col="frequency" sort={rr.sort} onSort={rr.toggle} /></th>
                  <th className="px-5 py-3 text-center"><SortBtn label="Employees" col="emp" sort={rr.sort} onSort={rr.toggle} /></th>
                  <th className="px-5 py-3 text-right"><SortBtn label="Gross" col="gross" sort={rr.sort} onSort={rr.toggle} /></th>
                  <th className="px-5 py-3 text-right"><SortBtn label="Deductions" col="ded" sort={rr.sort} onSort={rr.toggle} /></th>
                  <th className="px-5 py-3 text-right"><SortBtn label="Net" col="net" sort={rr.sort} onSort={rr.toggle} /></th>
                </tr>
              </thead>
              <tbody>
                {rr.sorted.map((r) => (
                  <tr
                    key={r.run_id}
                    data-testid={`dashboard-run-${r.run_id}`}
                    onClick={() => navigate(`/history/${r.run_id}`)}
                    className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors cursor-pointer"
                  >
                    <td className="px-5 py-3 font-medium">{r.company_name || "—"}</td>
                    <td className="px-5 py-3 font-mono">{r.pay_date}</td>
                    <td className="px-5 py-3">{FREQ_LABELS[r.pay_frequency]}</td>
                    <td className="px-5 py-3 text-center tabular-nums">{r.employee_count}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{money(r.totals?.gross)}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-red-600">{money(r.totals?.total_deductions)}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-emerald-600 font-medium">{money(r.totals?.net_pay)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-5 py-16 text-center text-sm text-muted-foreground">
            No payroll runs yet. Add a client company and employees to get started.
          </div>
        )}
      </div>
    </div>
  );
}
